import cx from 'classnames';
import { PropsWithChildren, useEffect, useState } from 'react';
import SVG from 'react-inlinesvg';
import ellipsisSvg from 'assets/icons/ellipsis.svg';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { ShowMoreMenuProps } from './ShowMoreMenu.types';

const ShowMoreMenu: React.FC<PropsWithChildren<ShowMoreMenuProps>> = ({
  onToggle,
  children,
  svgClassName,
  isOpen = false,
  isDisabled = false,
  align = 'end',
  dataTestId = 'show-more-menu',
  contentWidth = 'min-w-36',
  onClick,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    setIsMenuOpen(isOpen);
  }, [isOpen]);

  const handleOpenClose = (newState: boolean): void => {
    setIsMenuOpen(newState);
    onToggle?.(newState);
  };

  return (
    <div data-test-id={dataTestId} className="relative w-[24px] h-[24px]">
      <Popover
        containerClassName="z-50 py-1"
        align={align}
        positions={['bottom', 'top']}
        reposition
        isOpen={isMenuOpen}
        onClickOutside={() => handleOpenClose(false)}
        content={({ position, childRect, popoverRect }) => (
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowColor="var(--transparent-color)"
            arrowSize={0}
          >
            <div
              className={cx(
                'relative bg-neutral-50 shadow-md border border-neutral-300 rounded-md flex flex-col p-1',
                contentWidth,
              )}
              data-test-id="show-more-menu-actions-list"
            >
              {children}
            </div>
          </ArrowContainer>
        )}
      >
        <div
          className={cx(
            'w-6 h-6 flex hover:enabled:bg-primary-200 disabled:cursor-not-allowed items-center justify-center sub-header-base  rounded-md',
            {
              'bg-neutral-200': isMenuOpen,
              'bg-transparent border-transparent': !isMenuOpen,
              'cursor-not-allowed text-neutral-500': isDisabled,
              'text-primary': !isDisabled,
            },
          )}
          data-test-id="show-more-menu-button"
          role="presentation"
          onClick={(e) => {
            e.stopPropagation();

            if (isDisabled) return;
            handleOpenClose(!isMenuOpen);
            onClick?.();
          }}
        >
          <SVG src={ellipsisSvg} className={cx('h-4 w-4', svgClassName)} />
        </div>
      </Popover>
    </div>
  );
};

export default ShowMoreMenu;
